import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "../axios-orders";
import { PurchaseOrderTokenSummary, RespondOcTokenRequest } from "../types";
import { queryKeys } from "./queryKeys";

/**
 * Public (no-login) tokenised OC acceptance. Both calls go through the shared axios instance, whose
 * request interceptor only adds a Bearer header when an accessToken cookie exists - a logged-out
 * supplier has none, so these run unauthenticated. Nothing here needs the auth/user context.
 */

/**
 * Fetch the single-OC summary for an emailed token. 404 = unknown / expired / not yet sent;
 * 409 = already responded. Never retried or refetched (a refetch after responding would 409).
 */
export const useOcTokenDetails = (token: string | undefined) =>
  useQuery<PurchaseOrderTokenSummary>({
    queryKey: [queryKeys.OC_TOKEN_DETAILS, token],
    queryFn: async () => {
      const { data } = await axios.post("/purchase-orders/token/details", {
        token,
      });
      return data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

/** Accept / reject via the token (reject needs a reason). A used token answers 409. */
export const useRespondOcToken = () =>
  useMutation<unknown, Error, RespondOcTokenRequest>({
    mutationFn: async ({ token, accept, reason }) => {
      const body: RespondOcTokenRequest = { token, accept };
      if (!accept && reason) body.reason = reason;
      const { data } = await axios.post("/purchase-orders/token/respond", body);
      return data;
    },
  });
